import styled from '@emotion/styled'
import React, { ComponentProps, FC, Fragment } from 'react'
import ReactModal from 'react-modal'
import { Colors, ColorUtil } from '~/assets/style/colors'
import { ElevationLevelValues, Sizes } from '~/assets/style/tokens'
import { ScrollbarStyle } from '~/assets/style/utils'
import { WithClassName } from '~/types/utils'
import Button from './Button'
import { Fonts } from '~/assets/style/fonts'

type ModalProps = WithClassName & {
  onRequestClose: (event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => void
  showCloseButton?: boolean
  /**
   * Hand the modal's surface to the child: no white background, no padding, no chrome of its own — only the
   * scrim and, if asked for, the close button floated over the child's top-right corner.
   *
   * For content that is already a card and would otherwise sit inside a second one. The upgrade offer is
   * the case this exists for: the same card the locked pages draw over their blur, shown here over a scrim.
   */
  bare?: boolean
}

const ContentWrapper = styled.div<{ bare?: boolean }>`
  display: grid;
  row-gap: 18px;
  padding: 18px 24px 24px;
  grid-template-areas:
    'title close'
    'body body'
    'actions actions';
  grid-template-columns: 1fr min-content;
  grid-template-rows: min-content 1fr min-content;

  ${({ bare }) =>
    bare &&
    `
    display: block;
    padding: 0;
    position: relative;
  `}
`

/** Over the child's own corner, since in `bare` there is no chrome row to sit in. */
const BareCloseWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
`

const Title = styled.h4`
  grid-area: title;
  display: flex;
  align-items: center;
  margin: 0;
  padding-bottom: -${Sizes[4]};
  ${Fonts.P1};
`

const Body = styled.div<{ minWidth?: string }>`
  grid-area: body;
  padding-top: ${Sizes[4]};
  overflow: auto;
  min-width: ${({ minWidth }) => minWidth ?? '544px'};
  width: 100%;
  max-height: 600px;
  ${ScrollbarStyle}
`

const Actions = styled.div`
  grid-area: actions;
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  > :nth-last-of-type(3):first-of-type { // If 3 actions, we want the first to be at the left
    margin-right: auto;
  }
  
`

const CloseWrapper = styled.div`
  grid-area: close;
`

const CLOSE_TIMEOUT_MS = 250

const Container: FC<ComponentProps<typeof ReactModal> & ModalProps> = ({ children, onRequestClose, showCloseButton = true, bare = false, ...props }) => {
  ReactModal.setAppElement('body')

  const customStyles: ReactModal.Styles = {
    content: {
      // A `bare` modal is the offer card, which arrives on a page the reader has not asked anything of
      // yet, so it centres on the viewport itself rather than on the band under the header. The standard
      // dialogs keep the older offset: they answer a click somewhere on the page below, and moving every
      // one of them by 65px is not this modal's to decide.
      top: bare ? '50%' : 'calc(50% - 65px)', //middle of the screen minus header's height
      left: 'calc(50%)',
      transform: 'translate(-50%, -50%)',
      bottom: 'auto',
      right: 'auto',
      border: 'none',
      padding: 0,
      // The child is the surface in `bare`, so react-modal's own white box must not show behind its corners.
      ...(bare ? { background: 'transparent', overflow: 'visible' } : {}),
    },
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: ColorUtil.hexOpacity(Colors.Jet, 0.4),
      zIndex: ElevationLevelValues.high,
    },
  }
  return (
    <ReactModal closeTimeoutMS={CLOSE_TIMEOUT_MS} style={customStyles} onRequestClose={onRequestClose} {...props}>
      <ContentWrapper bare={bare}>
        {showCloseButton &&
          (bare ? (
            <BareCloseWrapper>
              <Button variant={'tertiary'} iconName={'close'} onClick={onRequestClose} trackingLabel={'common-modal-close'} />
            </BareCloseWrapper>
          ) : (
            <CloseWrapper>
              <Button variant={'tertiary'} iconName={'close'} onClick={onRequestClose} trackingLabel={'common-modal-close'} />
            </CloseWrapper>
          ))}
        {children}
      </ContentWrapper>
    </ReactModal>
  )
}
export const Modal = {
  Container,
  Content: Fragment,
  Title,
  Body,
  Actions,
  CLOSE_TIMEOUT_MS,
}
